"use client";
import React, { Fragment } from "react";
import Image, { StaticImageData } from "next/image";
import { Box, Icon, Circle, Stack, HStack } from "@chakra-ui/react";
import { IconType } from "@onemind-services-llc/react-icons-ng";
import { DefaultHoverEffect } from "styles/hoverPresets";
import { Heading, Text } from "@/elements";
import { useTextVariant } from "@/theme/hooks/useSectionStyle";

export type CardProps = {
  content?: React.ReactNode;
  icon?: IconType;
  iconWithCircle?: boolean;
  image?: string | StaticImageData;
  whiteImage?: string | StaticImageData;
  subTitle?: string;
  title: string;
  conclusion?: string;
  hoverEffect?: boolean;
  hover?: Record<string, any>; // optional custom hover styles
};

const Card = (props: CardProps) => {
  const {
    content,
    icon,
    iconWithCircle = true,
    image,
    whiteImage,
    subTitle,
    title,
    conclusion,
    hoverEffect = false,
    hover = DefaultHoverEffect,
  } = props;
  const textVariant = useTextVariant();
  return (
    <Stack
      // Add group class for _groupHover to work
      className={`brand-shadow group ${hoverEffect ? (whiteImage ? "whiteImg" : "") : ""}`}
      borderWidth={"1px"}
      borderColor={"primary.300"}
      backgroundColor={"white"}
      borderRadius={"32px"}
      p={6}
      gap={6}
      transition="transform 0.2s ease, box-shadow 0.3s ease, background 0.000001s ease"
      _hover={hoverEffect ? hover : undefined}
    >
      <HStack gap={4}>
        {icon &&
          (iconWithCircle ? (
            <Circle
              size="52px"
              border="2px solid"
              borderColor="primary.200"
              color="primary.200"
              _groupHover={
                hoverEffect && hover === DefaultHoverEffect
                  ? { color: "white", borderColor: "white" }
                  : {}
              }
            >
              <Icon as={icon} w={8} h={8} />
            </Circle>
          ) : (
            <Icon
              as={icon}
              w={8}
              h={8}
              color="primary.200"
              _groupHover={
                hoverEffect && hover === DefaultHoverEffect ? { color: "white" } : {}
              }
            />
          ))}
        {(image || whiteImage) && (
          <Box
            position="relative"
            minW={{ base: "40px", xl: "50px" }}
            minH={{ base: "40px", xl: "50px" }}
          >
            {image && <Image alt={title} src={image} fill />}
            {whiteImage && (
              <Image
                alt={`${title}-hover`}
                src={whiteImage}
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  transition: "opacity 0.3s ease",
                }}
                className="hover-image"
              />
            )}
          </Box>
        )}
        <Heading
          as="h4"
          variant={"subTitle1"}
          fontWeight={600}
          _groupHover={
            hoverEffect && hover === DefaultHoverEffect ? { color: "white" } : {}
          }
        >
          {title}
        </Heading>
      </HStack>
      {subTitle && (
        <Text
          color="text.100"
          fontWeight={550}
          variant={textVariant}
          _groupHover={
            hoverEffect && hover === DefaultHoverEffect ? { color: "white" } : {}
          }
        >
          {subTitle}
        </Text>
      )}
      {content && (
        <Fragment>
          {typeof content === "string" ? (
            <Text
              variant={textVariant}
              color={"text.100"}
              _groupHover={
                hoverEffect && hover === DefaultHoverEffect ? { color: "white" } : {}
              }
            >
              {content}
            </Text>
          ) : (
            <Box
              color="text.100"
              height={"100%"}
              _groupHover={
                hoverEffect && hover === DefaultHoverEffect ? { color: "white" } : {}
              }
            >
              {content}
            </Box>
          )}
        </Fragment>
      )}
      {conclusion && (
        <Text
          variant={textVariant}
          color="text.100"
          fontStyle={"italic"}
          _groupHover={
            hoverEffect && hover === DefaultHoverEffect ? { color: "white" } : {}
          }
        >
          {conclusion}
        </Text>
      )}
    </Stack>
  );
};

export default Card;
